import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import {
  getListingDescription,
  ListingBrokers,
  ListingDescription,
  ListingFiles,
  ListingIncentives,
  ListingParcelData,
  ListingSpaces,
} from '~/components/listing'
import { Tabs } from '~/components/ui'
import type { Client, Listing, ParcelProperties } from '~/models'

export default function ListingTabs({
  client,
  listing,
  parcels,
}: {
  client: Client
  listing: Listing
  parcels?: ParcelProperties[]
}) {
  const location = useLocation()

  const tabs = useMemo(() => {
    const items: {
      name: string
      value: string
      count?: number
      component: React.ReactNode
    }[] = []
    const description = getListingDescription(listing)
    if (description) {
      items.push({
        name: 'Overview',
        value: 'overview',
        component: <ListingDescription description={description} />,
      })
    }
    if (listing.files.length > 0) {
      items.push({
        name: 'Files',
        value: 'files',
        count: listing.files.length,
        component: <ListingFiles listing={listing} />,
      })
    }
    if (listing.suites.length > 1) {
      items.push({
        name: 'Spaces',
        value: 'spaces',
        count: listing.suites.length,
        component: <ListingSpaces listing={listing} />,
      })
    }
    if (listing.profiles.length > 0 || client.contact) {
      items.push({
        name:
          listing.profiles.length > 1 ||
          (listing.profiles.length > 0 && client.contact)
            ? 'Contacts'
            : 'Contact',
        value: 'contacts',
        count: listing.profiles.length + (client.contact ? 1 : 0),
        component: <ListingBrokers client={client} listing={listing} />,
      })
    }
    if (listing.incentives && listing.incentives.length > 0) {
      items.push({
        name: 'Incentives',
        value: 'incentives',
        count: listing.incentives.length,
        component: <ListingIncentives incentives={listing.incentives} />,
      })
    }
    if (client.parcel_data_enabled) {
      items.push({
        name: 'Parcel',
        value: 'parcel',
        component: <ListingParcelData listing={listing} parcels={parcels} />,
      })
    }
    return items
  }, [listing, client, parcels])

  const firstTab = tabs[0]?.value ?? 'overview'
  const [currentTab, setCurrentTab] = useState<string>(firstTab)

  useEffect(() => {
    setCurrentTab(firstTab)
  }, [location.pathname, firstTab])

  useEffect(() => {
    if (!tabs.some((tab) => tab.value === currentTab)) {
      setCurrentTab(firstTab)
    }
  }, [tabs, currentTab, firstTab])

  return (
    <Tabs value={currentTab} onValueChange={setCurrentTab}>
      <Tabs.List
        className="-ml-4 -mr-4 flex h-auto justify-start space-x-2 overflow-x-auto rounded-none bg-muted bg-opacity-20 px-4 py-2 text-base sm:-ml-6 sm:-mr-6 md:px-6 lg:space-x-4"
        aria-label="Listing sections">
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab.value}
            value={tab.value}
            className="relative flex items-center rounded-md px-1.5 py-0 text-base font-medium leading-[30px] data-[state=active]:bg-primary data-[state=active]:text-secondary dark:data-[state=active]:text-foreground sm:px-2">
            <span>{tab.name}</span>
            {tab.count && (
              <span className="ml-1 rounded-full bg-background px-[5px] text-xs text-foreground sm:block">
                {tab.count}
              </span>
            )}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {tabs.map((tab) => (
        <Tabs.Content key={tab.value} value={tab.value} className="pt-4">
          {tab.component}
        </Tabs.Content>
      ))}
    </Tabs>
  )
}
